import { CommandMenu, CommandMenuPage, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import { UNLABELLED_ASSET_LABEL } from "@overmap-ai/common"
import type { Asset, Stored } from "@overmap-ai/common"
import { useScanner } from "@overmap-ai/forms"
import { memo, type ReactElement, useCallback, useMemo, useState } from "react"

import { ASSET_FUSE_OPTIONS } from "../../fuse/assets"
import { useChangeEventHandler, useFuse } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import type { AssetAndIdentifiers } from "../../typings"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import type { ExtendableCommandMenuProps } from "./typings"

interface AssetCommandMenuProps extends Omit<ExtendableCommandMenuProps, "title"> {
	children?: ReactElement
	assetsAndIdentifiers: AssetAndIdentifiers[]
	onSelectAsset?: (asset: Stored<Asset>) => void
}

export const AssetCommandMenu = memo<AssetCommandMenuProps>((props) => {
	const {
		children,
		open,
		defaultOpen,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		assetsAndIdentifiers,
		onSelectAsset,
		...rest
	} = props

	const openScanner = useScanner()

	const [assetFilterValue, setAssetFilterValue] = useState("")

	const filteredAssets = useFuse<AssetAndIdentifiers>(assetsAndIdentifiers, assetFilterValue, ASSET_FUSE_OPTIONS)

	const assetsLimited = useMemo(() => {
		return filteredAssets.slice(0, 10)
	}, [filteredAssets])

	const handleAssetFilterChange = useChangeEventHandler(setAssetFilterValue)

	const handleScan = useCallback(() => {
		openScanner((close) => ({
			onDecodeResult: (result) => {
				setAssetFilterValue(result.rawValue.trim())
				close()
			},
			onClose: close,
		}))
	}, [openScanner, setAssetFilterValue])

	return (
		<CommandMenu.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <CommandMenu.Trigger asChild>{children}</CommandMenu.Trigger>}
			<CommandMenu.Overlay />
			<CommandMenu.Content title="Asset command menu" {...rest}>
				<CommandMenu.Pages defaultPage="assets">
					<CommandMenuPage page="assets">
						<div className="flex items-center justify-between gap-2">
							<CommandMenu.Input
								placeholder="Search"
								value={assetFilterValue}
								onChange={handleAssetFilterChange}
							/>
							<IconButton onClick={handleScan} variant="soft" className="mr-2" accentColor="base">
								<LuIcon icon={modelIconMapping.scan} />
							</IconButton>
						</div>
						<CommandMenu.Separator />
						<CommandMenu.Scroll>
							{assetsLimited.length > 0 ? (
								<CommandMenu.Group>
									<CommandMenu.GroupLabel>Assets</CommandMenu.GroupLabel>
									{assetsLimited.map(({ asset }) => {
										return (
											<CommandMenu.Item key={asset.uuid} onSelect={() => onSelectAsset?.(asset)}>
												<AssetTypeIcon assetTypeId={asset.asset_type} />
												<AssetIndexLabel assetId={asset.uuid} />
												<Text accentColor="base" className="truncate">
													{asset.label || UNLABELLED_ASSET_LABEL}
												</Text>
											</CommandMenu.Item>
										)
									})}
								</CommandMenu.Group>
							) : (
								<CommandMenu.Empty>No assets.</CommandMenu.Empty>
							)}
						</CommandMenu.Scroll>
					</CommandMenuPage>
				</CommandMenu.Pages>
			</CommandMenu.Content>
		</CommandMenu.Root>
	)
})
AssetCommandMenu.displayName = "AssetCommandMenu"
